import { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { Box, Button } from "@mui/material";
import { Trophy } from "lucide-react";
import { getExamResultsPayload } from 'utils/supabase';
import { useAuth } from "utils/AuthContext.js";
import { capitalizeWords } from "utils/text.js";
import { getChallengeInviteCode } from "utils/challengeLink";

// components

import ResultadosExamen from "components/Custom/ResultadosExamen.js";
import CardDesempenoEspecialidad from "components/Cards/CardDesempenoEspecialidad";
import CardFortalezas from "components/Custom/CardFortalezas";
import CardNavPreguntas from "components/Custom/CardNavPreguntas";
import ResultadosBottomActions from "components/Custom/ResultadosBottomActions";
import RetarOtrosButton from "components/Custom/RetarOtrosButton.js";

export default function Resultados() {
  const { examId } = useParams();
  const history = useHistory();
  const { user } = useAuth();
  const [resultsPayload, setResultsPayload] = useState(null);
  const challengeInviteCode = getChallengeInviteCode(examId);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [examId]);

  useEffect(() => {
    async function fetchExamResultsPayload() {
      try {
        const { data, error } = await getExamResultsPayload(examId);
        if (error) {
          console.error('Error fetching exam results payload:', error);
        } else {
          setResultsPayload(data);
        }
      } catch (error) {
        console.error('Unexpected error fetching exam results payload:', error);
      }
    }

    fetchExamResultsPayload();
  }, [examId]);

  const handleQuestionSelect = (item) => {
    if (!item?.question_id) return;
    history.push(`/admin/simulador/${examId}/preguntas/${item.question_id}`);
  };

  const handleReviewQuestions = () => {
    history.push(`/admin/simulador/${examId}/preguntas`);
  };

  const handleViewExplanations = () => {
    history.push(`/admin/simulador/${examId}/preguntas`);
  };

  const handleRetrySimulator = () => {
    history.push('/admin/examenes');
  };

  const handleGoDashboard = () => {
    history.push('/admin/dashboard');
  };

  return (
    <>
      <div className="flex flex-wrap mt-4">
        <Box
          sx={{
            width: "100%",
            mb: 3,
            px: 2,
            mx: { xs: "auto", md: 0 },
            maxWidth: { xs: "32rem", md: "none" },
            display: challengeInviteCode
              ? { xs: "grid", md: "flex" }
              : "flex",
            gridTemplateColumns: challengeInviteCode ? "repeat(2, minmax(0, 1fr))" : undefined,
            justifyContent: { xs: "center", md: "flex-end" },
            gap: { xs: 1, md: 1.5 },
          }}
        >
          <RetarOtrosButton
            examId={ examId }
            examTitle={ resultsPayload?.summary?.title }
            buttonSx={{
              width: { xs: "100%", sm: "auto" },
              minWidth: 0,
              px: { xs: 1, sm: 2.5 },
              py: { xs: 0.9, sm: 1.1 },
              fontSize: { xs: "0.76rem", sm: "0.875rem" },
              whiteSpace: "nowrap",
            }}
          />
          {challengeInviteCode ? (
            <Button
              variant="outlined"
              startIcon={<Trophy size={18} />}
              onClick={() => history.push(`/admin/reto/${challengeInviteCode}/resultados`)}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                borderRadius: "10px",
                py: { xs: 0.9, sm: 1.1 },
                px: { xs: 1, sm: 2.5 },
                width: { xs: "100%", sm: "auto" },
                minWidth: 0,
                fontSize: { xs: "0.76rem", sm: "0.875rem" },
                whiteSpace: "nowrap",
                color: "#7c3aed",
                borderColor: "#c4b5fd",
                "&:hover": { borderColor: "#a78bfa", backgroundColor: "#f5f3ff" },
              }}
            >
              Ver leaderboard
            </Button>
          ) : null}
        </Box>
        <div className="w-full mb-12 px-4">
          <ResultadosExamen resultsPayload={ resultsPayload } user={ capitalizeWords(user.user_metadata.full_name, true) } />
        </div>
        <div className="w-full lg:w-4/12 mb-6 px-4">
          <CardDesempenoEspecialidad resultsPayload={ resultsPayload } />
        </div>
        <div className="w-full lg:w-4/12 mb-6 px-4">
          <CardFortalezas resultsPayload={ resultsPayload } />
        </div>
        <div className="w-full lg:w-4/12 mb-6 px-4">
          <CardNavPreguntas
            resultsPayload={ resultsPayload }
            onQuestionSelect={ handleQuestionSelect }
          />
        </div>

        <div className="w-full px-4 mb-6">
          <ResultadosBottomActions
            onReviewQuestions={ handleReviewQuestions }
            onViewExplanations={ handleViewExplanations }
            onRetrySimulator={ handleRetrySimulator }
            onGoDashboard={ handleGoDashboard }
          />
        </div>
      </div>
    </>
  );
}
